(function (root, factory) {
  'use strict';
  const geometry = factory();
  if (typeof module === 'object' && module.exports) module.exports = geometry;
  if (!root || !root.customElements || root.customElements.get('ho-plant-hotspots')) return;
  let sequence = 0;
  let activeOwner = null;

  class PlantHotspots extends root.HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const start = () => this.initialise();
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true, signal: this.controller.signal });
      else start();
    }

    initialise() {
      if (!this.isConnected || this.image) return;
      const config = this.querySelector('[data-ho-plant-map]');
      try { this.map = JSON.parse(config.textContent); } catch (_) { return; }
      this.stage = this.closest('[data-ho-plant-stage]');
      this.image = this.stage && this.stage.querySelector('img');
      if (!this.image || !this.map.images || !this.map.plants) return;
      this.items = [];
      this.numbered = this.hasAttribute('data-numbered');
      this.active = null;
      this.tip = document.createElement('div');
      this.tip.className = 'ho-plant-tooltip';
      this.tip.id = 'ho-plant-tooltip-' + (++sequence);
      this.tip.setAttribute('role', 'tooltip');
      this.tip.hidden = true;
      this.append(this.tip);
      const listener = { signal: this.controller.signal };
      if (this.dataset.hoverImage) {
        this.originalSource = { src: this.image.getAttribute('src'), srcset: this.image.getAttribute('srcset') };
        this.detailVisible = false;
        this.pointerInside = false;
        this.stage.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') { this.pointerInside = true; this.setDetail(true); } }, listener);
        this.stage.addEventListener('pointerleave', event => { if (event.pointerType !== 'touch') { this.pointerInside = false; if (!this.stage.contains(document.activeElement)) this.setDetail(false); } }, listener);
        this.stage.addEventListener('focusin', () => this.setDetail(true), listener);
        this.stage.addEventListener('focusout', event => { if (!this.pointerInside && !this.stage.contains(event.relatedTarget)) this.setDetail(false); }, listener);
        this.stage.addEventListener('pointerdown', event => { if (event.pointerType === 'touch' && event.target === this.image) this.setDetail(!this.detailRequested); }, listener);
        if (!this.image.closest('button')) this.image.tabIndex = 0;
      }
      this.image.addEventListener('load', () => this.refresh(), listener);
      this.image.addEventListener('error', () => { if (this.detailVisible) { this.detailFailed = true; this.setDetail(false); } else this.clear(); }, listener);
      this.tip.addEventListener('pointerenter', () => clearTimeout(this.dismissTimer), listener);
      this.tip.addEventListener('pointerleave', () => this.deferHide(), listener);
      const dialog = this.closest('dialog');
      if (dialog) dialog.addEventListener('close', () => this.hide(), listener);
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.hide();
      }, listener);
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.active) { this.hide(); event.stopPropagation(); }
      }, listener);
      root.addEventListener('scroll', () => this.hide(), { ...listener, capture: true, passive: true });
      this.mutations = new MutationObserver(() => this.refresh());
      this.mutations.observe(this.image, { attributes: true, attributeFilter: ['src', 'srcset', 'width', 'height'] });
      this.resize = new ResizeObserver(() => this.layout());
      this.resize.observe(this.stage);
      this.resize.observe(this.image);
      this.refresh();
    }

    setDetail(visible) {
      this.detailRequested = visible;
      if (visible && !this.detailReady) {
        if (this.detailFailed || this.detailLoader) return;
        this.detailLoader = new root.Image();
        this.detailLoader.decoding = 'async';
        this.detailLoader.onload = () => {
          this.detailReady = true;
          if (this.isConnected && this.detailRequested) this.setDetail(true);
        };
        this.detailLoader.onerror = () => { this.detailFailed = true; this.detailRequested = false; };
        this.detailLoader.src = this.dataset.hoverImage;
        return;
      }
      if (this.detailVisible === visible) return;
      this.detailVisible = visible;
      this.hide();
      this.image.removeAttribute('srcset');
      this.image.src = visible ? this.dataset.hoverImage : this.originalSource.src;
      if (!visible && this.originalSource.srcset) this.image.setAttribute('srcset', this.originalSource.srcset);
      this.refresh();
    }

    clear() {
      this.hide();
      (this.items || []).forEach((item) => item.button.remove());
      this.items = [];
      this.currentMap = null;
    }

    refresh() {
      const source = geometry.filename(this.image.getAttribute('src'));
      const map = this.map.images.find((item) => item.file === source);
      if (!map) { this.clear(); return; }
      if (map === this.currentMap) { this.layout(); return; }
      this.clear();
      this.currentMap = map;
      map.points.forEach((point) => {
        const plant = this.map.plants[point.plant];
        if (!plant) return;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ho-plant-hotspot';
        button.setAttribute('aria-label', this.dataset.detailLabel.replace('__PLANT__', plant.name));
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-describedby', this.tip.id);
        const marker = document.createElement('span');
        marker.setAttribute('aria-hidden', 'true');
        marker.textContent = this.numbered ? String(point.plant + 1) : '';
        button.append(marker);
        this.append(button);
        const item = { button, plant, point };
        this.items.push(item);
        const listener = { signal: this.controller.signal };
        button.addEventListener('pointerenter', (event) => { if (event.pointerType !== 'touch') this.show(item); }, listener);
        button.addEventListener('pointerleave', () => this.deferHide(), listener);
        button.addEventListener('focus', () => this.show(item), listener);
        button.addEventListener('blur', () => this.hide(), listener);
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.show(item);
        }, listener);
      });
      this.layout();
    }

    layout() {
      if (!this.currentMap) return;
      const imageRect = this.image.getBoundingClientRect();
      const stageRect = this.getBoundingClientRect();
      if (!imageRect.width || !imageRect.height) {
        this.items.forEach(({ button }) => { button.hidden = true; });
        this.hide();
        return;
      }
      const style = root.getComputedStyle(this.image);
      const area = geometry.imageArea(imageRect.width, imageRect.height, this.currentMap.width, this.currentMap.height, style.objectFit, style.objectPosition);
      const visiblePlants = new Set();
      this.items.forEach(({ button, point }) => {
        const x = area.left + area.width * point.x / 100;
        const y = area.top + area.height * point.y / 100;
        button.hidden = (this.originalSource && !this.detailVisible) || !this.image.complete || this.image.naturalWidth === 0 || x < 0 || y < 0 || x > imageRect.width || y > imageRect.height;
        const primary = !button.hidden && !visiblePlants.has(point.plant);
        button.dataset.primary = String(primary);
        button.tabIndex = primary ? 0 : -1;
        if (!button.hidden) visiblePlants.add(point.plant);
        button.style.left = (imageRect.left - stageRect.left + x) + 'px';
        button.style.top = (imageRect.top - stageRect.top + y) + 'px';
        if (this.numbered && !primary) button.hidden = true;
        if (!this.numbered && point.w) button.style.width = Math.min(area.width * point.w / 100, 2 * Math.min(x, imageRect.width - x)) + 'px';
        if (!this.numbered && point.h) button.style.height = Math.min(area.height * point.h / 100, 2 * Math.min(y, imageRect.height - y)) + 'px';
      });
      if (this.active && this.active.button.hidden) this.hide();
      else if (this.active) this.placeTip();
    }

    show(item) {
      if (item.button.hidden) return;
      clearTimeout(this.dismissTimer);
      if (activeOwner && activeOwner !== this) activeOwner.hide();
      activeOwner = this;
      this.items.forEach(({ button }) => button.setAttribute('aria-expanded', String(button === item.button)));
      this.active = item;
      const label = document.createElement('span');
      label.className = 'ho-plant-tooltip__eyebrow';
      label.textContent = this.dataset.suppliedLabel;
      const name = document.createElement('strong');
      name.textContent = item.plant.name;
      const size = document.createElement('span');
      size.textContent = item.plant.size;
      this.tip.replaceChildren(label, name, size);
      this.tip.hidden = false;
      this.placeTip();
    }

    placeTip() {
      if (!this.active) return;
      const target = this.active.button.getBoundingClientRect();
      const tip = this.tip.getBoundingClientRect();
      const left = Math.max(12, Math.min(root.innerWidth - tip.width - 12, target.left + target.width / 2 - tip.width / 2));
      const top = target.top - tip.height - 5 >= 12 ? target.top - tip.height - 5 : Math.min(root.innerHeight - tip.height - 12, target.bottom + 5);
      this.tip.style.left = left + 'px';
      this.tip.style.top = Math.max(12, top) + 'px';
    }

    deferHide() {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = setTimeout(() => {
        if (!this.active || document.activeElement !== this.active.button) this.hide();
      }, 120);
    }

    hide() {
      clearTimeout(this.dismissTimer);
      if (this.active) this.active.button.setAttribute('aria-expanded', 'false');
      if (this.tip) this.tip.hidden = true;
      this.active = null;
      if (activeOwner === this) activeOwner = null;
    }

    disconnectedCallback() {
      if (this.originalSource && this.detailVisible) this.setDetail(false);
      if (this.detailLoader) { this.detailLoader.onload = null; this.detailLoader.onerror = null; }
      this.originalSource = null;
      this.detailLoader = null;
      this.detailReady = false;
      this.detailRequested = false;
      this.detailFailed = false;
      if (this.controller) this.controller.abort();
      if (this.mutations) this.mutations.disconnect();
      if (this.resize) this.resize.disconnect();
      this.clear();
      if (this.tip) this.tip.remove();
      this.controller = null;
      this.image = null;
    }
  }
  root.customElements.define('ho-plant-hotspots', PlantHotspots);
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';
  function filename(source) {
    try { return decodeURIComponent(new URL(source, 'https://example.invalid').pathname.split('/').pop()); }
    catch (_) { return ''; }
  }
  function imageArea(boxWidth, boxHeight, naturalWidth, naturalHeight, fit, position) {
    if (!naturalWidth || !naturalHeight || !boxWidth || !boxHeight) return { left: 0, top: 0, width: boxWidth, height: boxHeight };
    let width = boxWidth;
    let height = boxHeight;
    if (fit === 'contain' || fit === 'cover' || fit === 'scale-down' || fit === 'none') {
      let scale = fit === 'cover' ? Math.max(boxWidth / naturalWidth, boxHeight / naturalHeight) : Math.min(boxWidth / naturalWidth, boxHeight / naturalHeight);
      if (fit === 'none') scale = 1;
      if (fit === 'scale-down') scale = Math.min(1, scale);
      width = naturalWidth * scale;
      height = naturalHeight * scale;
    }
    const parts = String(position || '50% 50%').split(/\s+/);
    const offset = (value, space) => value.endsWith('%') ? space * parseFloat(value) / 100 : value === 'left' || value === 'top' ? 0 : value === 'right' || value === 'bottom' ? space : value === 'center' ? space / 2 : parseFloat(value) || 0;
    return { left: offset(parts[0], boxWidth - width), top: offset(parts[1] || '50%', boxHeight - height), width, height };
  }
  return { filename, imageArea };
});


